
import { useState, useContext } from 'react';
import { CMSContext } from 'context/AppContext';

import { SelectorContainer, SelectorTitle, SelectorSubtitle, OptionsContainer, Option, OptionImage, OptionCheckbox, OptionDetails, OptionTitle, OptionSubtitle, RegisterButton, LoginButton } from './CampaignTypeSelector.style';
import ArrowRightLineIcon from 'remixicon-react/ArrowRightLineIcon';
import { pushClickCtaEvent } from 'components/tracking/events';

type CampaignType = 'grow' | 'move' | 'host' | 'myow';
interface CampaignDetail {
  title: string
  description: string
  camp: 'g' | 'm' | 'h' | 'mw'
}

const CampaignTypeSelector = ({}) => {
    const [selectedOption, setSelectedOption] = useState<CampaignType | undefined>();
    const cms = useContext<any>(CMSContext);

    const translations = {
      title: cms.snippets["text-campaign-selector-title"]?.text || 'Here’s how you get involved',
      subtitle: cms.snippets["text-campaign-selector-subtitle"]?.text || 'Select a way to fundraise',
      growTitle: cms.snippets["text-campaign-selector-grow-title"]?.text || 'GROW A MO',
      growSubtitle: cms.snippets["text-campaign-selector-grow-subtitle"]?.text || 'Grow a Mo, raise funds, save lives.',
      moveTitle: cms.snippets["text-campaign-selector-move-title"]?.text || 'MOVE FOR MOVEMBER',
      moveSubtitle: cms.snippets["text-campaign-selector-move-subtitle"]?.text || cms.snippets["text-campaign-selector-move-subtitle-backup"]?.text || 'Run or walk 60km for the 60 men suicide takes globally every hour.',
      hostTitle: cms.snippets["text-campaign-selector-host-title"]?.text || 'HOST A MO-MENT',
      hostSubtitle: cms.snippets["text-campaign-selector-host-subtitle"]?.text || 'Throw an event to raise funds for men’s health.',
      myowTitle: cms.snippets["text-campaign-selector-myow-title"]?.text || 'MO YOUR OWN WAY',
      myowSubtitle: cms.snippets["text-campaign-selector-myow-subtitle"]?.text || 'Do dares. Climb mountains. Chase personal bests. Quit social media or choose your own challenge.',
      signUp: cms.snippets["text-campaign-selector-sign-up"]?.text || 'Sign up to fundraise',
      login: cms.snippets["text-campaign-selector-log-in"]?.text || 'Login (Already have an Account)'
    }

    const CAMPAIGN_TYPES: Record<CampaignType, CampaignDetail> = {
      grow: {
        title: translations.growTitle,
        description: translations.growSubtitle,
        camp: 'g'
      },
      move: {
        title: translations.moveTitle,
        description: translations.moveSubtitle,
        camp: 'm'
      },
      host: {
        title: translations.hostTitle,
        description: translations.hostSubtitle,
        camp: 'h'
      },
      myow: {
        title: translations.myowTitle,
        description: translations.myowSubtitle,
        camp: 'mw'
      }
    };

    const getLinkSuffix = (optionId?: CampaignType) => {
      if(!optionId) return '';
      const option = CAMPAIGN_TYPES[optionId];
      
      return option 
        ? `?prod_intent=${optionId}&mocamp=${option.camp}` 
        : '';
    }

    const getRegisterLink = (optionId?: CampaignType) => `${cms.siteSettings.campaignButtons.register.url}/${getLinkSuffix(optionId)}`;
    const getLoginLink = (optionId?: CampaignType) => `${cms.siteSettings.campaignButtons.login.url}/${getLinkSuffix(optionId)}`;

    const handleOptionToggle = (optionId: CampaignType) => {
        const newOption = selectedOption === optionId ? undefined : optionId;
        setSelectedOption(newOption);

        if(newOption){
          const option = CAMPAIGN_TYPES[optionId];
          pushClickCtaEvent({ linkText: option.title, linkUrl: getRegisterLink(optionId) })
        }
    };

    const handleButtonClick = (linkUrl: string, btnText: string) => {
      pushClickCtaEvent({linkText: btnText, linkUrl: linkUrl});
    };

    return <SelectorContainer>
      <div>
        <SelectorTitle>{translations.title}</SelectorTitle>
        <SelectorSubtitle>{translations.subtitle}</SelectorSubtitle>
      </div>
      
      <OptionsContainer>
        {Object.keys(CAMPAIGN_TYPES).map((key) => {
          const optionId = key as CampaignType;
          const option = CAMPAIGN_TYPES[optionId];

          return <Option key={optionId} onClick={() => handleOptionToggle(optionId)}>
              <OptionCheckbox
                type="checkbox"
                checked={selectedOption === optionId}
                onChange={() => {}} // Handled by parent div click
              />
            
              <OptionImage src={'/resources/images/content/campaignTypes/rego-' + optionId + '.jpg'} />
            
            <OptionDetails>
              <OptionTitle>{option.title}</OptionTitle>
              <OptionSubtitle>{option.description}</OptionSubtitle>
            </OptionDetails>
          </Option>
        })}
      </OptionsContainer>
      <RegisterButton href={getRegisterLink(selectedOption)} 
        onClick={() => handleButtonClick(getRegisterLink(selectedOption), translations.signUp)}>
          {translations.signUp}<ArrowRightLineIcon />
      </RegisterButton>
      <LoginButton href={getLoginLink(selectedOption)} 
        onClick={() => handleButtonClick(getLoginLink(selectedOption), translations.login)}>
          {translations.login}
      </LoginButton>
    </SelectorContainer>
}

export default CampaignTypeSelector;